import { useEffect, useRef, type ReactNode } from "react";
import { useUser } from "@clerk/react";
import { useUserProfile } from "@/hooks/use-user";
import { useUserStore } from "@/stores/user-store";
import { api } from "@/lib/api";
import * as Analytics from "@/services/analytics";

interface AuthSyncProviderProps {
  children: ReactNode;
}

async function getFingerprint(): Promise<string> {
  const FingerprintJS = await import("@fingerprintjs/fingerprintjs");
  const fp = await FingerprintJS.load();
  const result = await fp.get();
  return result.visitorId;
}

export function AuthSyncProvider({ children }: AuthSyncProviderProps) {
  const { isLoaded: clerkLoaded, isSignedIn, user: clerkUser } = useUser();
  const { user, isLoading, refetch } = useUserProfile();
  const { setUser, setLoading } = useUserStore();
  const hasIdentifiedRef = useRef(false);
  const hasSentFingerprintRef = useRef(false);

  useEffect(() => {
    if (!clerkLoaded) {
      setLoading(true);
      return;
    }
    if (!isSignedIn) {
      setUser(null);
      setLoading(false);
      return;
    }
    if (user) {
      setUser(user);
      setLoading(false);
    } else if (!isLoading) {
      setLoading(false);
    }
  }, [clerkLoaded, isSignedIn, user, isLoading, setUser, setLoading]);

  useEffect(() => {
    if (!clerkLoaded || !isSignedIn || !clerkUser || !user) return;
    if (hasSentFingerprintRef.current || user.fingerprint) return;
    hasSentFingerprintRef.current = true;

    getFingerprint()
      .then((fingerprint) =>
        api.post<{ success: boolean; credits: number; revoked?: boolean }>(
          `/users/${clerkUser.id}/fingerprint`,
          { fingerprint },
        ),
      )
      .then((res) => {
        if (res.revoked) {
          refetch();
        }
      })
      .catch(() => {});
  }, [clerkLoaded, isSignedIn, clerkUser, user, refetch]);

  useEffect(() => {
    if (!clerkLoaded || !isSignedIn || !clerkUser) return;

    // "User Signed Up" is fired server-side from the Clerk webhook
    // (exactly once per created user) — the client only identifies.
    if (!hasIdentifiedRef.current) {
      Analytics.identifyUser(clerkUser.id, {
        email: clerkUser.primaryEmailAddress?.emailAddress,
        username: clerkUser.username,
        first_name: clerkUser.firstName,
        last_name: clerkUser.lastName,
        created_at: clerkUser.createdAt?.toISOString(),
      });
      hasIdentifiedRef.current = true;
    }
  }, [clerkLoaded, isSignedIn, clerkUser]);

  return <>{children}</>;
}
